/**
 * The computer's live desktop in a sheet over the chat (slice 6c §4, §7.2,
 * §7.3; boards 92, 92b): Boat's stream in an iframe, under a layer that takes
 * every pointer and key event, so a watcher never clicks over the agent.
 * There's no Stop here: Stop stays in the chat's reply box (slice 1i), under
 * the sheet. A desktop sheet over the page from `md` up; full screen on a
 * phone, zoomed on the page with Fit/Zoom.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@crew/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { useWide } from "../../app/wide";
import { useLayoutAtOpen, useWatchLink, type Link } from "./desktop-link";
import { DesktopPill, EyeIcon } from "./marks";

/** "LIVE · VIEW ONLY", or "DONE" once the reply ended. */
const Pill = ({ done }: { done: boolean }) => (
  <DesktopPill dot={done ? null : "moss"}>{done ? "DONE" : "LIVE · VIEW ONLY"}</DesktopPill>
);

/** No picture: loading, why it failed (Try again, in control), or someone else has it. */
function NoStream({
  link,
  again,
  control,
  className,
}: {
  link: Exclude<Link, { state: "ready" }>;
  again: () => void;
  control: boolean;
  className?: string;
}) {
  const retry = link.state === "taken" || (link.state === "failed" && control);
  const label = link.state === "failed" ? "Try again" : control ? "Take it back" : "Watch again";
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 bg-[#1B2232] px-6 text-center text-body text-[#C9CEDA]",
        className,
      )}
    >
      {link.state === "loading" ? <Spinner className="size-5" /> : null}
      {link.state === "failed" ? <p role="alert">{link.text}</p> : null}
      {link.state === "taken" ? (
        <p data-desktop-taken>
          {link.name} opened the computer's screen. Only one person can see it at a time.
        </p>
      ) : null}
      {retry ? (
        <Button variant="secondary" onClick={again}>
          {label}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The stream, under the view-only layer. The iframe can't take focus or a
 * click: the layer covers it, and it's out of the tab order. On a phone the
 * layer still lets a finger pan the zoomed desktop (its scroll box is the
 * parent). In `control` (a taken handoff, §3.3) there's no layer: clicks and
 * keys reach the computer.
 */
export function Stream({
  link,
  again,
  control = false,
  className,
}: {
  link: Link;
  again: () => void;
  control?: boolean;
  className?: string;
}) {
  if (link.state !== "ready") {
    return <NoStream link={link} again={again} control={control} className={className} />;
  }
  return (
    <div className={cn("relative bg-[#1B2232]", className)}>
      <iframe
        title="The computer's screen"
        src={link.link.url}
        tabIndex={control ? 0 : -1}
        allow="autoplay; clipboard-read; clipboard-write; fullscreen"
        referrerPolicy="no-referrer"
        className="absolute inset-0 size-full border-0"
      />
      {control ? null : (
        <div
          data-view-only
          aria-hidden
          className="absolute inset-0"
          onPointerDown={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
        />
      )}
    </div>
  );
}

export function DesktopSheet({
  open,
  onClose,
  agentName,
  line,
  done,
}: {
  open: boolean;
  onClose: () => void;
  agentName: string;
  /** What the agent is doing: the turn's progress line. */
  line: string | null;
  /** The reply ended: the sheet says Done and offers Close. */
  done: boolean;
}) {
  const wide = useLayoutAtOpen(open);
  const { link, again } = useWatchLink(open);
  const title = done ? `${agentName} is done` : `${agentName} is using the computer`;
  const sub = done ? "Close this to read the reply." : (line ?? "Working");
  const parts = { title, sub, done, link, again, onClose };
  return (
    <SheetFrame open={open} onClose={onClose} mode="watch">
      {wide ? <WideSheet {...parts} /> : <PhoneSheet {...parts} />}
    </SheetFrame>
  );
}

/** The sheet itself: 1232 px over the page from `md` up, full screen on a phone (§7.2, §7.3). */
export function SheetFrame({
  open,
  onClose,
  mode,
  children,
}: {
  open: boolean;
  onClose: () => void;
  mode: "watch" | "control";
  children: ReactNode;
}) {
  const wide = useWide();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        data-desktop-sheet={mode}
        className={cn(
          "gap-0 bg-background p-0 ring-0",
          wide
            ? "w-auto max-w-none rounded-[14px] shadow-[0_24px_64px_#0F14214D] sm:max-w-none"
            : "inset-0 top-0 left-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col rounded-none sm:max-w-none",
        )}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

type Parts = {
  title: string;
  sub: string;
  done: boolean;
  link: Link;
  again: () => void;
  onClose: () => void;
};

/** The desktop at 1184×666 (0.62 of 1920×1080), or smaller to fit. */
export function WideStream({
  link,
  again,
  control = false,
}: {
  link: Link;
  again: () => void;
  control?: boolean;
}) {
  return (
    <Stream
      link={link}
      again={again}
      control={control}
      className="aspect-video w-[min(1184px,calc(100vw-7rem),calc((100dvh-12rem)*16/9))] overflow-clip rounded-lg"
    />
  );
}

/** Board 92: a 1232 px sheet, the desktop at 1184×666 (0.62 of 1920×1080), or smaller to fit. */
function WideSheet({ title, sub, done, link, again, onClose }: Parts) {
  return (
    <>
      <div className="flex h-[60px] shrink-0 items-center gap-3.5 border-b border-border pr-5 pl-6">
        <Pill done={done} />
        <div className="flex min-w-0 flex-col">
          <DialogTitle className="truncate text-[15px] leading-[18px] font-semibold text-foreground">
            {title}
          </DialogTitle>
          <DialogDescription
            data-desktop-line
            className="truncate text-xs leading-4 text-muted-foreground"
          >
            {sub}
          </DialogDescription>
        </div>
        <Button variant="ghost" className="ml-auto text-sm font-medium" onClick={onClose}>
          Close
        </Button>
      </div>
      <div className="px-6 pt-4">
        <WideStream link={link} again={again} />
      </div>
      <div className="flex h-[52px] shrink-0 items-center gap-2.5 px-6 text-meta leading-4 text-muted-foreground">
        <EyeIcon size={14} />
        View only while the crew works, so nobody clicks over it. To stop the reply, use Stop in
        this chat.
      </div>
    </>
  );
}

/** Board 92b: full screen, the desktop zoomed on the page (`PhoneStream`). */
function PhoneSheet({ title, sub, done, link, again, onClose }: Parts) {
  return (
    <>
      <div className="flex flex-col gap-2.5 px-[18px] pt-[max(env(safe-area-inset-top),6px)] pb-4">
        <div className="flex items-center justify-between">
          <Pill done={done} />
          <Button variant="ghost" className="-mr-2 h-11 text-[15px] font-medium" onClick={onClose}>
            Close
          </Button>
        </div>
        <DialogTitle className="text-[20px] leading-[26px] font-semibold tracking-tight text-foreground">
          {title}
        </DialogTitle>
        <DialogDescription data-desktop-line className="text-[15px] leading-[22px] text-copy">
          {sub}
        </DialogDescription>
      </div>
      <PhoneStream link={link} again={again} />
      <div className="flex items-center gap-2.5 px-[18px] pt-3.5 pb-[max(env(safe-area-inset-bottom),34px)] text-sm leading-5 text-muted-foreground">
        <EyeIcon size={18} />
        View only while the crew works, so nobody taps over it. To stop the reply, close this and
        use Stop in the chat.
      </div>
    </>
  );
}

/**
 * Board 92b's desktop, and 91b's: Zoom (the default) shows the browser's
 * page at a size a phone can read: the desktop's height fills the space,
 * Boat's panel and Chrome's toolbar (the top ~12%) above it, opening on its
 * middle, and a finger pans across. Fit shows the whole screen at the phone's
 * width.
 */
export function PhoneStream({
  link,
  again,
  control = false,
}: {
  link: Link;
  again: () => void;
  control?: boolean;
}) {
  const [zoom, setZoom] = useState(true);
  // Zoom opens on the middle of the screen, where a page's content usually is.
  const box = useRef<HTMLDivElement>(null);
  const ready = link.state === "ready";
  useEffect(() => {
    const el = box.current;
    if (el && zoom && ready) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, [zoom, ready]);
  return (
    <div className="relative min-h-0 flex-1 bg-[#1B2232]">
      <div
        ref={box}
        className={cn(
          "absolute inset-0",
          "flex overflow-y-hidden",
          zoom ? "overflow-x-auto" : "items-center overflow-x-hidden",
        )}
      >
        {zoom ? (
          <Stream
            link={link}
            again={again}
            control={control}
            className="h-[calc(100%/0.88)] shrink-0 -translate-y-[12%] [aspect-ratio:16/9]"
          />
        ) : (
          <Stream link={link} again={again} control={control} className="aspect-video w-full" />
        )}
      </div>
      <div className="absolute right-3 bottom-3 flex rounded-[9px] bg-[#1B2232E0] p-[3px]">
        {[
          ["Fit", false],
          ["Zoom", true],
        ].map(([label, value]) => (
          <button
            key={String(label)}
            type="button"
            aria-pressed={zoom === value}
            onClick={() => setZoom(value as boolean)}
            className={cn(
              "flex h-7 items-center rounded-[7px] px-3 text-meta leading-4",
              zoom === value
                ? "bg-white font-semibold text-[#1B2232]"
                : "font-medium text-[#C9CEDA]",
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
